import React, { useState, useEffect } from 'react';
import { Trash2, Eye, Users, Edit } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AdminLayout, AdminTable, AdminPagination, ConfirmationModal, GroupDetailModal } from '../../components/admin';
import { Button, Modal } from '../../components/ui';
import { toast } from '../../components/ui/Toaster';
import { adminGroupService } from '../../services/adminGroupService';

interface GroupData {
  id: number;
  name: string;
  description: string | null;
  maxMembers: number;
  createdAt: string;
  updatedAt: string;
  owner: {
    id: number;
    username: string;
    email: string;
  };
  event: {
    id: number;
    title: string;
    slug: string;
  } | null;
  memberCount: number;
  messageCount: number;
  lastMessageAt: string | null;
  isActive: boolean;
}

interface MessageData {
  id: number;
  message: string;
  sentAt: string;
  user: {
    id: number;
    username: string;
    profilePhoto: string | null;
  };
}

interface GroupsResponse {
  groups: GroupData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface MessagesResponse {
  group: {
    id: number;
    name: string;
    totalMessages: number;
  };
  messages: MessageData[];
  members: Array<{
    id: number;
    username: string;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const GroupManagementPage: React.FC = () => {
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Chat modal
  const [chatModal, setChatModal] = useState<{
    isOpen: boolean;
    group: GroupData | null;
    messages: MessageData[];
    members: Array<{ id: number; username: string }>;
    selectedUserId: number | null;
    messagesLoading: boolean;
    messagesPagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>({
    isOpen: false,
    group: null,
    messages: [],
    members: [],
    selectedUserId: null,
    messagesLoading: false,
    messagesPagination: { page: 1, limit: 50, total: 0, totalPages: 0 }
  });
  
  // Delete modal
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    group: GroupData | null;
    loading: boolean;
  }>({
    isOpen: false,
    group: null,
    loading: false
  });

  // Group Detail Modal
  const [groupDetailModal, setGroupDetailModal] = useState<{
    isOpen: boolean;
    groupId: string;
  }>({
    isOpen: false,
    groupId: ''
  });

  useEffect(() => {
    loadGroups();
  }, [pagination.page, pagination.limit, sortBy, sortOrder]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder
      };
      
      const response: GroupsResponse = await adminGroupService.getAllGroups(params);
      
      setGroups(response.groups);
      setPagination(response.pagination);
    } catch (error: any) {
      console.error('Failed to load groups:', error);
      toast.error('Fout', 'Kon groepen niet laden');
    } finally {
      setLoading(false);
    }
  };

  const loadGroupMessages = async (group: GroupData, userId?: number, page = 1, append = false) => {
    try {
      setChatModal(prev => ({ ...prev, messagesLoading: true }));
      
      const params: any = {
        page,
        limit: 50
      };
      
      if (userId) {
        params.userId = userId;
      }
      
      const response: MessagesResponse = await adminGroupService.getGroupMessages(group.id, params);
      
      setChatModal(prev => ({
        ...prev,
        messages: append ? [...response.messages, ...prev.messages] : response.messages,
        members: response.members,
        messagesLoading: false,
        messagesPagination: response.pagination
      }));
    } catch (error: any) {
      console.error('Failed to load group messages:', error);
      toast.error('Fout', 'Kon groep berichten niet laden');
      setChatModal(prev => ({ ...prev, messagesLoading: false }));
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleViewChat = (group: GroupData) => {
    setChatModal({
      isOpen: true,
      group,
      messages: [],
      members: [],
      selectedUserId: null,
      messagesLoading: true,
      messagesPagination: { page: 1, limit: 50, total: 0, totalPages: 0 }
    });
    loadGroupMessages(group);
  };

  const handleUserFilter = (userId: number | null) => {
    if (chatModal.group) {
      setChatModal(prev => ({ 
        ...prev, 
        selectedUserId: userId,
        messagesPagination: { ...prev.messagesPagination, page: 1 }
      }));
      loadGroupMessages(chatModal.group, userId || undefined, 1);
    }
  };

  const handleLoadMoreMessages = () => {
    if (chatModal.group && !chatModal.messagesLoading) {
      const nextPage = chatModal.messagesPagination.page + 1;
      loadGroupMessages(chatModal.group, chatModal.selectedUserId || undefined, nextPage, true);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deleteModal.group) return;
    
    try {
      setDeleteModal(prev => ({ ...prev, loading: true }));
      
      await adminGroupService.deleteGroup(deleteModal.group.id);
      
      toast.success('Succes', 'Groep succesvol verwijderd');
      setDeleteModal({ isOpen: false, group: null, loading: false });
      
      // Reload groups
      loadGroups();
    } catch (error: any) {
      console.error('Failed to delete group:', error);
      toast.error('Fout', error.message || 'Kon groep niet verwijderen');
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const formatMessageDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      key: 'name',
      title: 'Groep',
      sortable: true,
      render: (value: string, group: GroupData) => (
        <Link
          to={`/groups/${group.id}`}
          className="sf-link hover:underline max-w-48 truncate block"
          title={value}
        >
          {value}
        </Link>
      )
    },
    {
      key: 'event',
      title: 'Evenement',
      render: (event: any) => (
        <div className="text-sm max-w-32 truncate">
          {event ? (
            <span className="text-neon-cyan" title={event.title}>{event.title}</span>
          ) : (
            <span className="italic text-fg-faint">Geen evenement</span>
          )}
        </div>
      )
    },
    {
      key: 'memberCount',
      title: 'Leden',
      render: (count: number, group: GroupData) => (
        <span className="text-sm font-medium text-fg-body">{count}/{group.maxMembers}</span>
      )
    },
    {
      key: 'isActive',
      title: 'Status',
      render: (isActive: boolean) => (
        <span
          className={
            isActive
              ? 'sf-badge-lime'
              : 'inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-muted'
          }
        >
          {isActive ? 'Actief' : 'Inactief'}
        </span>
      )
    }
  ];

  const renderActions = (group: GroupData) => (
    <div className="flex items-center space-x-2">
      <button
        onClick={() => setGroupDetailModal({
          isOpen: true,
          groupId: group.id.toString()
        })}
        className="p-2 min-h-[44px] min-w-[44px] rounded-xl text-neon-cyan hover:bg-neon-cyan/10 transition-colors"
        title="Bewerk groepdetails"
      >
        <Edit className="h-4 w-4" />
      </button>
      <button
        onClick={() => handleViewChat(group)}
        className="p-2 min-h-[44px] min-w-[44px] rounded-xl text-neon-violet hover:bg-neon-violet/10 transition-colors"
        title="Bekijk chat log"
      >
        <Eye className="h-4 w-4" />
      </button>
      <button
        onClick={() => setDeleteModal({ isOpen: true, group, loading: false })}
        className="p-2 min-h-[44px] min-w-[44px] rounded-xl text-neon-pink-soft hover:bg-neon-pink/10 transition-colors"
        title="Verwijder groep"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );

  return (
    <AdminLayout 
      title="Groep beheer" 
      subtitle="Beheer groepen en monitor chat activiteit"
    >
      {/* Groups Table */}
      <AdminTable
        columns={columns}
        data={groups}
        loading={loading}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        actions={renderActions}
        emptyMessage="Geen groepen gevonden"
      />

      {/* Pagination */}
      {!loading && groups.length > 0 && (
        <div className="mt-6">
          <AdminPagination
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        </div>
      )}

      {/* Chat Modal */}
      <Modal
        isOpen={chatModal.isOpen}
        onClose={() => setChatModal(prev => ({ ...prev, isOpen: false }))}
        size="xl"
      >
        {chatModal.group && (
          <div>
            <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-4">
              <div>
                <h3 className="text-lg font-bold text-fg-bright">
                  {chatModal.group.name} - Chat log
                </h3>
                <p className="text-sm text-fg-muted">
                  {chatModal.messagesPagination.total} totaal berichten
                </p>
              </div>

              {/* User Filter */}
              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-2">
                <label className="text-sm text-fg-body">Filter op gebruiker:</label>
                <select
                  value={chatModal.selectedUserId || ''}
                  onChange={(e) => handleUserFilter(e.target.value ? parseInt(e.target.value) : null)}
                  className="rounded-field border border-white/10 bg-white/[.04] px-3 py-2 text-sm text-fg min-h-[44px] focus:border-neon-pink/40 focus:outline-none"
                >
                  <option value="">Alle gebruikers</option>
                  {chatModal.members.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.username}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Messages */}
            <div className="rounded-item border border-white/[.06] bg-white/[.02] p-4 max-h-96 overflow-y-auto">
              {chatModal.messagesLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-neon-cyan"></div>
                  <p className="text-sm text-fg-muted mt-2">Berichten laden...</p>
                </div>
              ) : chatModal.messages.length === 0 ? (
                <div className="text-center py-8 italic text-fg-muted">
                  Geen berichten gevonden
                </div>
              ) : (
                <div className="space-y-3">
                  {chatModal.messages.map((message) => (
                    <div key={message.id} className="sf-item p-3">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {message.user.profilePhoto ? (
                            <img
                              className="h-8 w-8 rounded-full object-cover"
                              src={message.user.profilePhoto}
                              alt={message.user.username}
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-white/[.08] flex items-center justify-center">
                              <Users className="h-4 w-4 text-fg-faint" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-sm text-fg-bright">
                              {message.user.username}
                            </span>
                            <span className="font-mono text-[11px] text-fg-meta">
                              {formatMessageDate(message.sentAt)}
                            </span>
                          </div>
                          <p className="text-sm text-fg-body mt-1">{message.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Load More Button */}
              {!chatModal.messagesLoading && 
               chatModal.messagesPagination.page < chatModal.messagesPagination.totalPages && (
                <div className="mt-4 text-center">
                  <Button
                    variant="outline"
                    onClick={handleLoadMoreMessages}
                    disabled={chatModal.messagesLoading}
                  >
                    Meer berichten laden ({chatModal.messagesPagination.total - chatModal.messages.length} resterend)
                  </Button>
                </div>
              )}
            </div>
            
            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                onClick={() => setChatModal(prev => ({ ...prev, isOpen: false }))}
              >
                Sluiten
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => !deleteModal.loading && setDeleteModal({ isOpen: false, group: null, loading: false })}
        onConfirm={handleDeleteGroup}
        title="Groep verwijderen"
        message={`Weet je zeker dat je "${deleteModal.group?.name}" wilt verwijderen? Dit verwijdert permanent de groep en alle berichten. Deze actie kan niet ongedaan worden gemaakt.`}
        confirmText="Groep verwijderen"
        type="danger"
        loading={deleteModal.loading}
      />

      {/* Group Detail Modal */}
      <GroupDetailModal
        isOpen={groupDetailModal.isOpen}
        onClose={() => setGroupDetailModal({ isOpen: false, groupId: '' })}
        groupId={groupDetailModal.groupId}
        onGroupUpdated={loadGroups}
      />
    </AdminLayout>
  );
};

export default GroupManagementPage;